import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Lock, Upload, X, FileText, Image, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  {
    id: 'avatar',
    title: 'Die Zielperson (Avatar)',
    subtitle: 'Damit wir Kampagnen erstellen, die Interessenten emotional abholen.',
    fields: [
      { id: 'avatar_situation', label: 'Aktuelle Situation', placeholder: 'Wer ist der ideale Kandidat? (z.B. Angestellte im mittleren Management, die "raus aus dem Hamsterrad" wollen, oder Fachkräfte, die sich endlich für die eigene Tasche krumm machen möchten?)' },
      { id: 'avatar_psychologie', label: 'Psychologie / Größter Schmerz', placeholder: 'Was ist deren größter Schmerz im aktuellen Job? (z.B. mangelnde Wertschätzung, gedeckeltes Gehalt, keine Freiheit?)' },
      { id: 'avatar_motivation', label: 'Primärer Trigger', placeholder: 'Was ist der primäre Trigger für den Wechsel? (Eigener Chef sein, finanzielle Freiheit, Sicherheit durch ein erprobtes System?)' },
      { id: 'avatar_demografie', label: 'Demografische Merkmale', placeholder: 'Alter, Geschlecht, Region, Einkommenslevel der Zielgruppe? (Hilft beim Ad-Targeting)' },
    ],
  },
  {
    id: 'usp',
    title: 'USP & Angebot',
    subtitle: 'Die Alleinstellungsmerkmale, die dein Angebot unwiderstehlich machen.',
    fields: [
      { id: 'usp_hauptargument', label: 'Größter USP', placeholder: 'Warum sollte jemand Franchise-Partner werden und nicht einfach auf eigene Faust gründen oder zu einem Mitbewerber gehen?' },
      { id: 'usp_nobrainer', label: 'Das "No-Brainer" Argument', placeholder: 'Welchen Teil der Arbeit nehmt ihr dem Partner komplett ab? (z.B. "Wir liefern die Kunden, du machst nur die Beratung" oder "Schlüsselfertiges System in 30 Tagen")' },
      { id: 'usp_social_proof', label: 'Beweise (Social Proof)', placeholder: 'Gibt es Erfolgsstorys von Menschen, die diesen Schritt mit euch schon gegangen sind? (z.B. "Vom Fliesenleger zum Agenturinhaber")' },
      { id: 'usp_bisheriges_marketing', label: 'Bisherige Marketing-Aktivitäten', placeholder: 'Was wurde bisher schon gemacht? (Ads, Landing Pages, Videos, Agenturen?) Damit wir wissen, was schon getestet wurde.' },
    ],
  },
  {
    id: 'huerden',
    title: 'Hürden & Einwände',
    subtitle: 'Das hilft uns, die Ad-Optimierung und den Lead-Filter zu verfeinern.',
    fields: [
      { id: 'huerden_bedenken', label: 'Einstiegshürden', placeholder: 'Was ist das größte Bedenken der Interessenten? (z.B. "Ich habe kein technisches Vorwissen" oder "Ich habe Angst vor dem Risiko"). Wie entkräften wir das?' },
      { id: 'huerden_finanzen', label: 'Finanzieller Rahmen', placeholder: 'Ab welcher Investitionssumme ist ein Einstieg realistisch? (Wichtig für das Filtern in der Lead-Form)' },
    ],
  },
  {
    id: 'quali',
    title: 'Qualifizierungs-Logik',
    subtitle: 'Damit wir den Lead-Filter korrekt einrichten.',
    fields: [
      { id: 'quali_zeitrahmen', label: 'Zeitlicher Rahmen', placeholder: 'Muss der Interessent sofort voll einsteigen oder ist ein nebenberuflicher Start möglich?' },
      { id: 'quali_mindestanforderungen', label: 'Mindestanforderungen', placeholder: 'Welche Soft-Skills sind zwingend erforderlich (z.B. Kommunikationsstärke, Fleiß), wenn Fachwissen keine Rolle spielt?' },
    ],
  },
  {
    id: 'marke',
    title: 'Marke & Tonalität',
    subtitle: 'Wie soll eure Marke in den Kampagnen rüberkommen?',
    fields: [
      { id: 'marke_stimme', label: 'Markenstimme', placeholder: 'Wie soll die Kommunikation klingen? (z.B. Seriös/Professional, Locker/Kumpelhaft, Motivierend/Empowerment, Provokant/Disruptiv)' },
      { id: 'marke_verboten', label: 'Verbotene Begriffe / Sensibilitäten', placeholder: 'Gibt es Wörter, Themen oder Versprechen, die auf keinen Fall verwendet werden dürfen?' },
    ],
  },
];

/* WPP Consulting Fragebogen — Fördermittel-Leads für Gründer */
const SECTIONS_WPP = [
  {
    id: 'zielgruppe',
    title: 'Zielgruppe & Gründer-Profil',
    subtitle: 'Wen wollen wir mit den Kampagnen erreichen?',
    fields: [
      { id: 'zielgruppe_wer', label: 'Wer ist dein idealer Kunde?', placeholder: 'z.B. Erstgründer, Seriengründer, Selbstständige die expandieren, Angestellte die gründen wollen? Branche? Alter? Region?' },
      { id: 'zielgruppe_phase', label: 'In welcher Phase befinden sich die Gründer?', placeholder: 'z.B. Noch angestellt mit Idee, bereits gekündigt, schon gegründet aber brauchen Kapital, gescheiterte Erstanträge?' },
      { id: 'zielgruppe_schmerz', label: 'Größter Schmerz / Frustration', placeholder: 'Was hält sie aktuell zurück? (z.B. kein Kapital, Bürokratie-Angst, Businessplan abgelehnt, Überforderung mit Förderstellen?)' },
      { id: 'zielgruppe_trigger', label: 'Was löst die Handlung aus?', placeholder: 'Was bringt sie dazu, sich jetzt Hilfe zu suchen? (z.B. Kündigung steht bevor, Deadline für Förderantrag, Bankgespräch gescheitert?)' },
    ],
  },
  {
    id: 'angebot',
    title: 'Dein Angebot & USP',
    subtitle: 'Was macht deine Beratung einzigartig?',
    fields: [
      { id: 'angebot_kern', label: 'Kernangebot', placeholder: 'Was genau bietest du an? (Businessplan-Erstellung, Förderantrag, Begleitung bis Auszahlung, alles zusammen?)' },
      { id: 'angebot_usp', label: 'Größter USP', placeholder: 'Warum du und nicht ein anderer Berater oder eine Online-Vorlage? (z.B. persönliche Begleitung, 15+ Jahre Erfahrung, Erfolgsquote, Kosten werden gefördert?)' },
      { id: 'angebot_ergebnis', label: 'Konkretes Ergebnis / Versprechen', placeholder: 'Was kann der Kunde erwarten? (z.B. "30.000-800.000€ in ca. 3 Monaten auf dem Konto", "Förderfähiger Businessplan in 2 Wochen")' },
      { id: 'angebot_social_proof', label: 'Erfolgsgeschichten / Referenzen', placeholder: 'Welche konkreten Ergebnisse hast du erzielt? (z.B. "AK Medtec — Patent + Kapital seit 2017", Anzahl erfolgreicher Förderanträge?)' },
    ],
  },
  {
    id: 'huerden',
    title: 'Einwände & Qualifizierung',
    subtitle: 'Was müssen wir in den Ads entkräften und wie filtern wir?',
    fields: [
      { id: 'huerden_einwaende', label: 'Häufigste Einwände', placeholder: 'Was sagen Interessenten, bevor sie buchen? (z.B. "Ist das seriös?", "Kann ich mir die Beratung leisten?", "Bin ich überhaupt förderfähig?")' },
      { id: 'huerden_kosten', label: 'Kostenstruktur', placeholder: 'Was kostet deine Beratung? Kann sie gefördert werden? Erstgespräch kostenlos? (Wichtig für die Ad-Kommunikation)' },
      { id: 'huerden_disqualifikation', label: 'Wer ist NICHT geeignet?', placeholder: 'Welche Leads willst du vermeiden? (z.B. kein Eigenkapital, unrealistische Ideen, bereits insolvent?)' },
      { id: 'huerden_foerderkriterien', label: 'Grundvoraussetzungen für Förderung', placeholder: 'Welche Kriterien müssen erfüllt sein? (z.B. Wohnsitz Deutschland, bestimmte Rechtsform, Mindest-Investitionssumme?)' },
    ],
  },
  {
    id: 'kampagne',
    title: 'Kampagne & Tonalität',
    subtitle: 'Wie sollen wir kommunizieren?',
    fields: [
      { id: 'kampagne_ton', label: 'Markenstimme / Tonalität', placeholder: 'Wie soll die Kommunikation klingen? (z.B. Seriös & vertrauenswürdig, Nahbar & kumpelhaft, Motivierend & empowernd?)' },
      { id: 'kampagne_region', label: 'Geografischer Fokus', placeholder: 'Wo sollen die Ads ausgespielt werden? (Sachsen, gesamt Deutschland, DACH?)' },
      { id: 'kampagne_bisherig', label: 'Bisheriges Marketing', placeholder: 'Was wurde bisher gemacht? (Website, Google Ads, Social Media, Empfehlungen?) Was hat funktioniert, was nicht?' },
      { id: 'kampagne_verboten', label: 'Verbotene Begriffe / No-Gos', placeholder: 'Gibt es Begriffe oder Versprechen die nicht verwendet werden dürfen? (z.B. Garantien, bestimmte Summen?)' },
    ],
  },
];

/* Target TV Fragebogen — Regionale TV-Werbung für KMUs */
const SECTIONS_TARGETTV = [
  {
    id: 'unternehmen',
    title: 'Ihr Unternehmen',
    subtitle: 'Grundlegende Informationen, damit wir die Kampagne optimal ausrichten.',
    fields: [
      { id: 'tt_branche', label: 'Branche & Geschäftsmodell', placeholder: 'In welcher Branche sind Sie tätig? Was ist Ihr Kernprodukt/-service? (z.B. Autohaus, Restaurant-Kette, Handwerksbetrieb, Einzelhandel, Dienstleister...)' },
      { id: 'tt_einzugsgebiet', label: 'Einzugsgebiet / Region', placeholder: 'Wo befinden sich Ihre Kunden? (Stadt, PLZ-Gebiet, Bundesland, mehrere Standorte?) Wie groß ist Ihr Einzugsgebiet?' },
      { id: 'tt_zielgruppe', label: 'Zielgruppe', placeholder: 'Wen möchten Sie erreichen? (Alter, Geschlecht, Interessen, B2B oder B2C, Einkommensklasse?)' },
      { id: 'tt_alleinstellung', label: 'Alleinstellungsmerkmal / USP', placeholder: 'Was unterscheidet Sie von Mitbewerbern? Warum sollten Kunden zu Ihnen kommen statt zur Konkurrenz?' },
    ],
  },
  {
    id: 'werbeziel',
    title: 'Werbeziel & Botschaft',
    subtitle: 'Was soll die TV-Kampagne konkret erreichen?',
    fields: [
      { id: 'tt_ziel', label: 'Primäres Werbeziel', placeholder: 'Was ist das Hauptziel? (z.B. Bekanntheit steigern, Neukunden gewinnen, Produkt-Launch, Saisonangebot bewerben, Mitarbeiter finden?)' },
      { id: 'tt_botschaft', label: 'Kernbotschaft / Angebot', placeholder: 'Welche Botschaft soll beim Zuschauer ankommen? Gibt es ein konkretes Angebot? (z.B. "Jetzt 20% Eröffnungsrabatt", "Ihr regionaler Partner seit 30 Jahren")' },
      { id: 'tt_cta', label: 'Gewünschte Handlung (Call-to-Action)', placeholder: 'Was soll der Zuschauer nach dem Spot tun? (Website besuchen, anrufen, vorbeikommen, QR-Code scannen?)' },
      { id: 'tt_zeitraum', label: 'Gewünschter Kampagnenzeitraum', placeholder: 'Wann soll die Kampagne laufen? (Startdatum, Dauer, saisonale Relevanz? z.B. "Ab September, 4 Wochen" oder "Dauerhaft")' },
    ],
  },
  {
    id: 'material',
    title: 'Bestehendes Material & Budget',
    subtitle: 'Was ist bereits vorhanden und was ist geplant?',
    fields: [
      { id: 'tt_spot', label: 'TV-Spot vorhanden?', placeholder: 'Gibt es bereits einen fertigen TV-Spot? Wenn ja: Länge (15s/20s/30s), Format, wo gehostet? Oder muss ein Spot produziert werden?' },
      { id: 'tt_material', label: 'Vorhandenes Werbematerial', placeholder: 'Welches Material existiert bereits? (Logo, Bilder, Videos, Imagefilm, Social Media Ads, Broschüren?) Bitte auch unter "Dokumente hochladen" anhängen.' },
      { id: 'tt_bisherige_werbung', label: 'Bisherige Werbemaßnahmen', placeholder: 'Welche Werbung wurde bisher gemacht? (Online-Ads, Print, Radio, Social Media?) Was hat gut funktioniert, was nicht?' },
      { id: 'tt_budget', label: 'Budget-Rahmen', placeholder: 'Welches monatliche oder Gesamt-Budget ist für die TV-Kampagne eingeplant? (Grobe Orientierung reicht, z.B. "2.000-5.000€/Monat")' },
    ],
  },
  {
    id: 'sonstiges',
    title: 'Sonstiges & Tonalität',
    subtitle: 'Letzte Details für die optimale Kampagnenplanung.',
    fields: [
      { id: 'tt_sender', label: 'Bevorzugte Sender', placeholder: 'Gibt es Sender-Präferenzen? (z.B. RTL, VOX, ProSieben, SAT.1, DMAX, Sport1?) Oder sollen wir basierend auf der Zielgruppe empfehlen?' },
      { id: 'tt_tonalitaet', label: 'Tonalität & Stil', placeholder: 'Wie soll die Werbung wirken? (Seriös, emotional, humorvoll, premium, regional-authentisch?) Gibt es Referenzen/Spots die Ihnen gefallen?' },
      { id: 'tt_wettbewerber', label: 'Wettbewerber', placeholder: 'Wer sind Ihre Hauptwettbewerber in der Region? Machen diese bereits TV-Werbung?' },
      { id: 'tt_sonstiges', label: 'Weitere Anmerkungen', placeholder: 'Gibt es sonst noch etwas, das wir wissen sollten? Besondere Wünsche, Einschränkungen, Termine?' },
    ],
  },
];

/* LionX Digital Fragebogen — Terminvereinbarung für Webdesign/SEO/Social Media */
const SECTIONS_LIONX = [
  {
    id: 'unternehmen',
    title: 'Über Ihr Unternehmen',
    subtitle: 'Damit wir die richtigen Interessenten für Sie ansprechen können.',
    fields: [
      { id: 'lx_kernleistung', label: 'Kernleistungen & Fokus', placeholder: 'Welche Services bieten Sie primär an? (z.B. Webdesign, SEO, Social Media Management, Google Ads?) Gibt es Pakete oder Einzelleistungen?' },
      { id: 'lx_preisrahmen', label: 'Preisrahmen Ihrer Leistungen', placeholder: 'In welcher Range liegen Ihre Angebote? (z.B. "Website ab 3.000€", "SEO-Retainer ab 500€/Monat") — Wichtig für die Vorqualifizierung.' },
      { id: 'lx_einzugsgebiet', label: 'Einzugsgebiet / Markt', placeholder: 'Nur Wien? Ganz Österreich? DACH? Arbeiten Sie auch remote mit Kunden außerhalb Ihrer Region?' },
      { id: 'lx_kapazitaet', label: 'Aktuelle Kapazität', placeholder: 'Wie viele neue Projekte können Sie pro Monat realistisch annehmen? Haben Sie ein Team oder arbeiten Sie allein?' },
    ],
  },
  {
    id: 'zielkunde',
    title: 'Der ideale Neukunde',
    subtitle: 'Wen sollen wir als Termin für Sie gewinnen?',
    fields: [
      { id: 'lx_zielbranche', label: 'Zielbranchen', placeholder: 'Welche Branchen funktionieren am besten für Sie? (z.B. Gastronomie, Handwerker, Ärzte/Praxen, E-Commerce, lokale Dienstleister, Startups?)' },
      { id: 'lx_zielgroesse', label: 'Unternehmensgröße', placeholder: 'Eher Einzelunternehmer, KMUs mit 5-50 Mitarbeitern oder größere Firmen? Wer hat Budget und Entscheidungsgewalt?' },
      { id: 'lx_schmerzpunkt', label: 'Typische Schmerzpunkte', placeholder: 'Was sind die häufigsten Probleme Ihrer Zielkunden? (z.B. "Website sieht veraltet aus", "werden bei Google nicht gefunden", "Social Media bringt nichts")' },
      { id: 'lx_kaufsignal', label: 'Kaufsignale / Trigger', placeholder: 'Wann entscheiden sich Unternehmen typischerweise für Ihre Leistung? (z.B. Neueröffnung, Relaunch, schlechte Google-Bewertungen, Konkurrenz ist besser aufgestellt?)' },
    ],
  },
  {
    id: 'termin',
    title: 'Das Erstgespräch',
    subtitle: 'Wie soll der Termin mit dem Interessenten ablaufen?',
    fields: [
      { id: 'lx_terminablauf', label: 'Was erwartet den Kunden im Erstgespräch?', placeholder: 'Wie läuft Ihr typisches Erstgespräch ab? (z.B. kurze Analyse der aktuellen Website, Bedarf ermitteln, konkreten Vorschlag machen?) Was nimmt der Kunde aus dem Termin mit?' },
      { id: 'lx_usp', label: 'Warum sollte jemand den Termin wahrnehmen?', placeholder: 'Was macht Ihr Erstgespräch wertvoll? (z.B. kostenloser Website-Check, SEO-Quick-Audit, konkreter Maßnahmenplan?) Was bekommen Interessenten, das sie woanders nicht bekommen?' },
      { id: 'lx_einwaende', label: 'Häufigste Einwände vor der Terminbuchung', placeholder: 'Was hält Interessenten davon ab, einen Termin zu buchen? (z.B. "Habe schon eine Website", "Kein Budget gerade", "Mein Neffe macht das") — Wie entkräften wir das?' },
      { id: 'lx_referenzen', label: 'Erfolgsbeispiele die überzeugen', placeholder: 'Welche konkreten Ergebnisse können wir nennen? (z.B. "Kunde X hat nach 3 Monaten SEO 3x mehr Anfragen", "Website-Relaunch hat Conversion um 40% gesteigert")' },
    ],
  },
  {
    id: 'material',
    title: 'Material & Sonstiges',
    subtitle: 'Vorhandenes Material und letzte Details.',
    fields: [
      { id: 'lx_bestehendes', label: 'Vorhandenes Marketing-Material', placeholder: 'Gibt es bereits Case Studies, Angebotsvorlagen, Pitch Decks, Vorher/Nachher-Beispiele? Bitte auch unten hochladen.' },
      { id: 'lx_tonalitaet', label: 'Tonalität & Ansprache', placeholder: 'Wie soll die Ansprache gegenüber Interessenten klingen? (z.B. Locker & kreativ, Professionell & beratend, Direkt & ergebnisorientiert?) Duzen oder Siezen?' },
      { id: 'lx_verfuegbarkeit', label: 'Terminverfügbarkeit / Calendly-Link', placeholder: 'Wann sind Sie für Erstgespräche verfügbar? Falls vorhanden, bitte Ihren Buchungslink einfügen (z.B. Calendly, Cal.com).' },
      { id: 'lx_sonstiges', label: 'Weitere Anmerkungen', placeholder: 'Alles was wir sonst noch wissen sollten — besondere Wünsche, Erfahrungen mit früheren Akquise-Partnern, No-Gos etc.' },
    ],
  },
];

/* Sam Plett Fragebogen — Makler-Wechsel aus MLM/Strukturvertrieb */
const SECTIONS_SAMPLETT = [
  {
    id: 'zielgruppe',
    title: 'Die Zielperson',
    subtitle: 'Wen genau wollen wir mit den Kampagnen ansprechen?',
    fields: [
      { id: 'sp_avatar', label: 'Idealer Kandidat', placeholder: 'Wer ist der perfekte Lead? (z.B. Strukturvertriebler bei DVAG, MLP, Tecis, Swiss Life Select, OVB, Bonnfinanz, Horbach, Deutsche Vermögensberatung, oder Ausschließlichkeitsvermittler bei Allianz, Ergo, HUK, Debeka?) Bitte alle relevanten Vertriebe/Gesellschaften auflisten. Alter, Erfahrung, aktueller Status?' },
      { id: 'sp_schmerz', label: 'Größter Schmerz im aktuellen System', placeholder: 'Was frustriert diese Menschen am meisten? (z.B. gedeckelte Provisionen, kein eigener Bestand, Abhängigkeit vom Strukturvertrieb, keine unternehmerische Freiheit, Druck von oben?)' },
      { id: 'sp_trigger', label: 'Was löst den Wechselwunsch aus?', placeholder: 'Wann fangen die an zu suchen? (z.B. Provision wurde gekürzt, Führungskraft hat gewechselt, Kollegen sind gegangen, eigene Kunden dürfen nicht mitgenommen werden?)' },
      { id: 'sp_disqualifikation', label: 'Wer ist NICHT geeignet?', placeholder: 'Welche Leads sollen wir vermeiden? (z.B. ohne 34d, ohne Kundenstamm, unter X Jahren Erfahrung, nur Teilzeit-Interesse?)' },
    ],
  },
  {
    id: 'angebot',
    title: 'Das Angebot & USP',
    subtitle: 'Was macht den Wechsel zu Sam Plett unwiderstehlich?',
    fields: [
      { id: 'sp_usp', label: 'Größter USP gegenüber "alleine wechseln"', placeholder: 'Warum sollte jemand mit Sam Plett wechseln statt einfach alleine Makler zu werden? (z.B. Leads, Backoffice, rechtliche Absicherung, Mentoring, Netzwerk mit 500+ Gesellschaften?)' },
      { id: 'sp_nobrainer', label: 'Das No-Brainer Argument', placeholder: 'Was überzeugt am schnellsten? (z.B. "Kostenlose Leads ab Tag 1", "Wir übernehmen die komplette Wechselhilfe inkl. Rechtsanwalt", "5-stellige Monatsumsätze innerhalb von X Monaten"?)' },
      { id: 'sp_social_proof', label: 'Stärkste Erfolgsgeschichten', placeholder: 'Welche konkreten Ergebnisse können wir in den Ads/Landingpages nennen? (z.B. "Max war bei DVAG, jetzt macht er 15k/Monat als freier Makler") — Echte Zahlen und Namen wenn möglich.' },
      { id: 'sp_erstgespraech', label: 'Was erwartet den Lead im Erstgespräch?', placeholder: 'Was passiert im Expertengespräch? (z.B. individuelle Analyse der aktuellen Situation, konkreter Wechselfahrplan, Provisionsvergleich?) Was nimmt der Lead mit, auch wenn er nicht wechselt?' },
    ],
  },
  {
    id: 'einwaende',
    title: 'Einwände & Hürden',
    subtitle: 'Was hält potenzielle Wechsler zurück?',
    fields: [
      { id: 'sp_angst', label: 'Größte Angst vor dem Wechsel', placeholder: 'Wovor haben die meisten am meisten Angst? (z.B. Kunden verlieren, Stornohaftung, rechtliche Konsequenzen, Konkurrenzklausel, finanzielles Loch in der Übergangszeit?)' },
      { id: 'sp_einwaende', label: 'Häufigste Einwände', placeholder: 'Was sagen Interessenten bevor sie buchen? (z.B. "Mein Vertrag bindet mich noch X Jahre", "Ich habe Angst vor der Stornohaftung", "Ich bin zu alt für einen Wechsel", "Mein Team kommt nicht mit")' },
      { id: 'sp_entkraeftung', label: 'Wie entkräften wir diese?', placeholder: 'Was sind die besten Gegenargumente? (z.B. "Unsere Anwälte prüfen deinen Vertrag kostenlos", "Wir übernehmen die Stornohaftungsrisiken", "Das Durchschnittsalter unserer Wechsler ist 42")' },
      { id: 'sp_mindestvoraussetzung', label: 'Mindestvoraussetzungen', placeholder: 'Was muss ein Kandidat mitbringen? (z.B. 34d vorhanden, mindestens X Jahre Erfahrung, bestehender Kundenstamm, Bereitschaft zum Vollzeit-Wechsel?)' },
    ],
  },
  {
    id: 'kampagne',
    title: 'Kampagne & Material',
    subtitle: 'Details für die Kampagnenstrategie.',
    fields: [
      { id: 'sp_tonalitaet', label: 'Tonalität & Ansprache', placeholder: 'Wie soll die Kommunikation klingen? (z.B. Motivierend & empowernd, Sachlich & vertrauensvoll, Provokant & direkt?) Duzen oder Siezen? Welche Sprache spricht die Zielgruppe?' },
      { id: 'sp_verboten', label: 'No-Gos & Sensibilitäten', placeholder: 'Was darf auf keinen Fall gesagt werden? Gibt es Begriffe die vermieden werden müssen? Dürfen konkrete Vertriebe (DVAG, Tecis etc.) namentlich genannt werden in den Ads?' },
      { id: 'sp_material', label: 'Vorhandenes Material', placeholder: 'Welches Material existiert bereits? (Testimonial-Videos, Case Studies, Landingpages, bestehende Ads, Presseberichte?) Bitte auch unten hochladen.' },
      { id: 'sp_calendly', label: 'Terminverfügbarkeit / Calendly-Link', placeholder: 'Wann sind Sie für Erstgespräche verfügbar? Bitte Ihren Buchungslink einfügen (z.B. calendly.com/plettsam).' },
    ],
  },
];

/* LiveJobs AG Fragebogen — Employer Branding Videos & Social Recruiting */
const SECTIONS_LIVEJOBS = [
  {
    id: 'unternehmen',
    title: 'Über LiveJobs',
    subtitle: 'Grundinfos damit wir die Kampagne optimal auf Ihre Dienstleistung zuschneiden.',
    fields: [
      { id: 'lj_kernleistung', label: 'Hauptleistungen & Pakete', placeholder: 'Welche Services stehen im Fokus? (z.B. Jobvideos, Social Recruiting Kampagnen, Employer Branding Beratung, Workshops, Onboarding-Videos?) Gibt es Pakete oder wird individuell kalkuliert?' },
      { id: 'lj_preisrahmen', label: 'Preisrahmen', placeholder: 'In welcher Größenordnung liegen Ihre Projekte? (z.B. "Jobvideo ab CHF X", "Social Recruiting Kampagne ab CHF X/Monat") — Wichtig für die Vorqualifizierung der Leads.' },
      { id: 'lj_einzugsgebiet', label: 'Einzugsgebiet', placeholder: 'Nur Deutschschweiz? Ganze Schweiz? DACH? Produzieren Sie auch vor Ort in Österreich/Deutschland oder nur remote?' },
      { id: 'lj_kapazitaet', label: 'Aktuelle Kapazität', placeholder: 'Wie viele neue Projekte können Sie pro Monat aufnehmen? Gibt es saisonale Engpässe?' },
    ],
  },
  {
    id: 'zielkunde',
    title: 'Der ideale Kunde',
    subtitle: 'Wen sollen wir als Termin für LiveJobs gewinnen?',
    fields: [
      { id: 'lj_zielbranchen', label: 'Zielbranchen & Unternehmenstypen', placeholder: 'Welche Branchen/Unternehmen haben den größten Bedarf? (z.B. Gesundheitswesen/Spitäler, Industrie, öffentliche Verwaltung, Pflege, Logistik, IT?) KMUs oder auch Großunternehmen?' },
      { id: 'lj_entscheider', label: 'Wer ist der Entscheider?', placeholder: 'Wer sitzt am Tisch? (HR-Leiter, Geschäftsführer, Recruiting Manager, Employer Branding Verantwortliche?) In welcher Unternehmensgröße gibt es Budget für Employer Branding Videos?' },
      { id: 'lj_schmerz', label: 'Typische Probleme der Zielkunden', placeholder: 'Was frustriert HR-Abteilungen aktuell am meisten? (z.B. zu wenig qualifizierte Bewerbungen, hohe Fluktuation, schlechtes Arbeitgeberimage, Stelleninserate funktionieren nicht mehr, Konkurrenz ist sichtbarer?)' },
      { id: 'lj_kaufsignal', label: 'Wann wird es dringend?', placeholder: 'Welche Trigger führen dazu, dass Unternehmen aktiv nach einer Lösung suchen? (z.B. offene Stellen seit Monaten unbesetzt, Mitarbeiter kündigen, Wachstumsphase, neue Abteilung, Arbeitgebermarke aufbauen?)' },
    ],
  },
  {
    id: 'termin',
    title: 'Das Beratungsgespräch',
    subtitle: 'Was erwartet den Kunden und wie überzeugen wir ihn?',
    fields: [
      { id: 'lj_terminablauf', label: 'Was passiert im Beratungsgespräch?', placeholder: 'Wie läuft ein typisches Erstgespräch ab? (z.B. Analyse der aktuellen Recruiting-Strategie, Showreel zeigen, konkreter Vorschlag für Jobvideo/Kampagne?) Was nimmt der Kunde mit, auch wenn er nicht bucht?' },
      { id: 'lj_usp', label: 'Warum LiveJobs und nicht die Konkurrenz?', placeholder: 'Was unterscheidet LiveJobs? (z.B. 15+ Jahre Erfahrung, Referenzen wie SBB/Flughafen Zürich/Schindler, eigenes Filmteam, LVR Methode, kein Template-Ansatz, Schweizer Marktkenntnis?)' },
      { id: 'lj_einwaende', label: 'Häufigste Einwände', placeholder: 'Was hält Entscheider davon ab zu buchen? (z.B. "Zu teuer", "Wir machen das intern", "Videos brauchen wir nicht", "Unsere Branche ist nicht fotogen", "Budget erst nächstes Jahr")' },
      { id: 'lj_referenzen', label: 'Stärkste Referenzen & Ergebnisse', placeholder: 'Welche Erfolgsbeispiele überzeugen am schnellsten? (z.B. "Spital X hat mit unserem Jobvideo 3x mehr Bewerbungen bekommen", "Werkprüfer-Vakanz innerhalb kurzer Zeit besetzt") — Konkrete Zahlen wenn möglich.' },
    ],
  },
  {
    id: 'material',
    title: 'Kampagne & Material',
    subtitle: 'Letzte Details für die Lead-Generierung.',
    fields: [
      { id: 'lj_tonalitaet', label: 'Tonalität der Ansprache', placeholder: 'Wie soll die Kommunikation gegenüber HR-Entscheidern klingen? (z.B. Professionell & beratend, Kreativ & inspirierend, Direkt & ergebnisorientiert?) Duzen oder Siezen?' },
      { id: 'lj_bisheriges', label: 'Bisherige Marketing-Aktivitäten', placeholder: 'Was wurde bisher zur Lead-Generierung gemacht? (LinkedIn, Google Ads, Empfehlungen, Messen, Content Marketing?) Was hat funktioniert, was nicht?' },
      { id: 'lj_material', label: 'Vorhandenes Material', placeholder: 'Gibt es bereits Case Studies, Showreel, Pitch Deck, bestehende Ads, Testimonial-Videos? Bitte auch unten hochladen.' },
      { id: 'lj_calendly', label: 'Terminverfügbarkeit / Buchungslink', placeholder: 'Wann sind Sie für Beratungsgespräche verfügbar? Bitte Ihren Buchungslink einfügen falls vorhanden.' },
    ],
  },
];

/* allectio consulting Fragebogen — International Executive Search */
const SECTIONS_ALLECTIO = [
  {
    id: 'positionierung',
    title: 'Ihre Positionierung & Fokus',
    subtitle: 'Damit wir die richtigen Mandanten für allectio ansprechen.',
    fields: [
      { id: 'ac_kernkompetenz', label: 'Kernkompetenzen — was macht allectio besonders?', placeholder: 'Welche Bereiche der Personalberatung stehen im Fokus? (z.B. Executive Search für C-Level, RPO, New Placement, Wirtschaftsmediation?) Welche Kompetenz soll in der Kampagne am stärksten kommuniziert werden?' },
      { id: 'ac_branchen', label: 'Stärkste Branchen & Sektoren', placeholder: 'In welchen Branchen haben Sie die meisten Erfolge und das stärkste Netzwerk? (z.B. IT/Digital Transformation, Automotive, Energy, Health, Manufacturing?) Gibt es Branchen die Sie aktiv ausbauen wollen?' },
      { id: 'ac_geografie', label: 'Geografischer Fokus der Kampagne', placeholder: 'Welche Märkte sollen adressiert werden? (DACH, EMEA, global?) Gibt es Regionen mit besonders hoher Nachfrage oder wo Sie Marktanteile gewinnen möchten?' },
      { id: 'ac_differenzierung', label: 'Was unterscheidet allectio von anderen Executive Search Boutiquen?', placeholder: 'Warum sollte ein Unternehmen allectio beauftragen statt Heidrick & Struggles, Korn Ferry oder eine andere Boutique? (z.B. Branchenexpertise, persönliche Betreuung, internationales Netzwerk, Exklusivmandate, 25+ Jahre Erfahrung?)' },
    ],
  },
  {
    id: 'zielmandat',
    title: 'Das ideale Mandat',
    subtitle: 'Welche Art von Suchaufträgen möchten Sie gewinnen?',
    fields: [
      { id: 'ac_zielunternehmen', label: 'Ideales Mandanten-Profil', placeholder: 'Welche Unternehmen sind die attraktivsten Mandanten? (Konzerne, Mittelstand, Scale-ups? Bestimmte Branchen? Mindestgröße? Internationale Organisationen mit DACH-Bedarf?)' },
      { id: 'ac_entscheider', label: 'Wer entscheidet über die Beauftragung?', placeholder: 'Wer sitzt am Tisch? (CHRO, CEO, VP HR, Head of Talent Acquisition, Aufsichtsrat?) In welcher Unternehmensgröße wird typischerweise extern gesucht?' },
      { id: 'ac_positionen', label: 'Welche Positionen wollen Sie primär besetzen?', placeholder: 'Welche Rollen sind am lukrativsten und haben die höchste Nachfrage? (C-Level: CIO/CTO/CDO, VP-Level, Managing Consultants, Senior IT-Spezialisten?) Gibt es Nischen-Positionen wo allectio besonders stark ist?' },
      { id: 'ac_schmerzpunkt', label: 'Typische Probleme der Mandanten', placeholder: 'Was frustriert HR-Abteilungen und Geschäftsführer bei der Besetzung von Führungspositionen? (z.B. kein Zugang zu passiven Kandidaten, Fehlbesetzungen, zu lange Time-to-Hire, fehlende Marktkenntnis in Nischenmärkten?)' },
    ],
  },
  {
    id: 'termin',
    title: 'Das Mandantengespräch',
    subtitle: 'Wie überzeugen wir potenzielle Mandanten von allectio?',
    fields: [
      { id: 'ac_erstgespraech', label: 'Was erwartet den Mandanten im Erstgespräch?', placeholder: 'Wie läuft ein typisches Kennenlernen ab? (z.B. Analyse des Suchprofils, Einschätzung der Marktlage, Vorstellung der allectio-Methodik, Referenz-Cases?) Was nimmt der Mandant mit, auch wenn er nicht sofort beauftragt?' },
      { id: 'ac_referenzen', label: 'Stärkste Referenzen & Erfolgsbeispiele', placeholder: 'Welche Mandanten/Projekte überzeugen am schnellsten? (z.B. bekannte Konzerne, besonders schwierige Besetzungen die gelungen sind, Time-to-Hire Benchmarks?) Dürfen Namen genannt werden oder nur anonymisiert?' },
      { id: 'ac_einwaende', label: 'Häufigste Einwände potenzieller Mandanten', placeholder: 'Was hält Entscheider davon ab, allectio zu beauftragen? (z.B. "Wir suchen intern", "Zu teuer", "Haben schon einen Headhunter", "Brauchen erst Budgetfreigabe", "Kennen allectio nicht") — Wie entkräften Sie diese?' },
      { id: 'ac_honorarmodell', label: 'Honorarmodell & Konditionen', placeholder: 'Wie ist die Preisstruktur? (Retainer + Success Fee, reines Contingency, RPO-Modelle?) Gibt es Garantien bei Frühfluktuation? Was macht das Modell attraktiv für neue Mandanten?' },
    ],
  },
  {
    id: 'kampagne',
    title: 'Kampagne & Ansprache',
    subtitle: 'Letzte Details für die Lead-Generierung.',
    fields: [
      { id: 'ac_tonalitaet', label: 'Tonalität & Markenstimme', placeholder: 'Wie soll allectio in der Ansprache wirken? (z.B. Premium & exklusiv, Nahbar & partnerschaftlich, Direkt & ergebnisorientiert?) Siezen ist Standard — gibt es Ausnahmen? Deutsch und/oder Englisch?' },
      { id: 'ac_bisheriges', label: 'Bisherige Akquise-Kanäle', placeholder: 'Wie gewinnen Sie heute Mandanten? (Empfehlungen, LinkedIn, Events/Konferenzen, Direktansprache, Website?) Was funktioniert gut, was soll ergänzt werden?' },
      { id: 'ac_material', label: 'Vorhandenes Material', placeholder: 'Gibt es bereits Imagebroschüren, Case Studies, Testimonials, LinkedIn-Beiträge, Videos, Pitch Decks? Bitte auch unten hochladen — besonders die Imagebroschüre.' },
      { id: 'ac_calendly', label: 'Terminverfügbarkeit / Buchungslink', placeholder: 'Wann ist Herr Spörer oder das Team für Erstgespräche verfügbar? Gibt es einen Buchungslink? Bevorzugte Kanäle (Telefon, Video, persönlich)?' },
    ],
  },
];

/* B2CyberSec Fragebogen — IT-Security, NIS-2 Compliance & Pentesting */
const SECTIONS_B2CYBERSEC = [
  {
    id: 'positionierung',
    title: 'B2CyberSec — Positionierung & Schwerpunkte',
    subtitle: 'Damit wir die Kampagne auf die richtige Säule fokussieren.',
    fields: [
      { id: 'b2_schwerpunkt', label: 'Welche Säule soll im Fokus der Kampagne stehen?', placeholder: 'Was soll primär beworben werden? (1) Professional Services — IT-Experten on demand (2) NIS-2 & Compliance — Readiness Assessments, ISO 27001, DORA (3) Penetration Testing — Webapp, Infrastruktur, Red Team. Oder alle drei? Gewichtung?' },
      { id: 'b2_usp', label: 'Was macht B2CyberSec anders als die Big-4 oder andere Cyber-Boutiquen?', placeholder: 'Was soll in den Ads am stärksten rüberkommen? (z.B. 60+ Jahre kombinierte Erfahrung, nur Senior-Leute ab Tag 1, kein Berater-Sprech, CISSP-zertifizierter Ex-CISO, spezialisiertes Pentest-Team in SEE, persönliche Betreuung?)' },
      { id: 'b2_referenzen', label: 'Stärkste Referenzen & Zahlen', placeholder: 'Welche Erfolge können wir in der Kampagne nennen? (z.B. 200+ Projekte, DAX-Konzerne, Telkos, bestimmte Branchen? Gibt es Case Studies die wir nutzen dürfen?) Dürfen Kundennamen genannt werden?' },
      { id: 'b2_preisrahmen', label: 'Angebotsstruktur & Preisrahmen', placeholder: 'Welche Angebotsmodelle gibt es? (z.B. Einmaliges Readiness Assessment, Retainer-Modell für laufende Compliance-Betreuung, Pentest-Pakete, monatlicher Security-as-a-Service?) Gibt es ein Retainer-Produkt das Sie stärker pushen wollen? Typische Einstiegspreise pro Modell?' },
    ],
  },
  {
    id: 'zielkunde',
    title: 'Der ideale Kunde',
    subtitle: 'Wen sollen wir mit den Kampagnen erreichen?',
    fields: [
      { id: 'b2_zielunternehmen', label: 'Ideales Kundenprofil', placeholder: 'Welche Unternehmen sind die besten Kunden? (z.B. Mittelstand 50-500 MA die unter NIS-2 fallen, Konzerne die Pentesting brauchen, IT-Dienstleister die Security outsourcen?) Mindestgröße? Branchen?' },
      { id: 'b2_entscheider', label: 'Wer trifft die Kaufentscheidung?', placeholder: 'Wen müssen wir adressieren? (Geschäftsführer wegen persönlicher NIS-2 Haftung, CISO, IT-Leiter, Head of Compliance, CFO wegen Budgetfreigabe?) Unterschiede je nach Säule?' },
      { id: 'b2_nis2_branchen', label: 'NIS-2 relevante Zielbranchen', placeholder: 'Welche der 18 NIS-2 Sektoren wollen Sie priorisieren? (Energie, Gesundheit, Banken/Versicherungen, IT-Dienste, Logistik, Lebensmittel, Produktion, Chemie?) Wo haben Sie die meiste Erfahrung?' },
      { id: 'b2_schmerzpunkt', label: 'Was treibt die Zielkunden gerade um?', placeholder: 'Welche Ängste/Probleme sind aktuell am größten? (z.B. NIS-2 Deadline verpasst + persönliche GF-Haftung, kein CISO vorhanden, letzte Security-Audit gescheitert, Cyberangriff bei Wettbewerber, Kunden fordern Nachweise?)' },
    ],
  },
  {
    id: 'termin',
    title: 'Das Erstgespräch',
    subtitle: 'Was passiert wenn ein Lead den Termin bucht?',
    fields: [
      { id: 'b2_erstgespraech', label: 'Was erwartet den Kunden im Erstgespräch?', placeholder: 'Wie läuft ein typisches 30-Min Gespräch ab? (z.B. NIS-2 Betroffenheits-Check, Security-Posture Einschätzung, konkreter Maßnahmenplan?) Was bekommt der Kunde kostenlos mit — auch wenn er nicht bucht?' },
      { id: 'b2_team', label: 'Wer führt die Erstgespräche?', placeholder: 'Boris (Professional Services), Senad (NIS-2/Compliance), Bojan (Pentesting) — oder wird erstmal zentral qualifiziert und dann zugewiesen? Sollen Termine direkt über die jeweiligen Calendly-Links laufen?' },
      { id: 'b2_einwaende', label: 'Häufigste Einwände potenzieller Kunden', placeholder: 'Was hält Unternehmen davon ab zu buchen? (z.B. "NIS-2 betrifft uns nicht", "Haben intern einen IT-Leiter", "Budget erst nächstes Jahr", "Machen wir mit bestehender IT-Beratung", "Pentest hatten wir schon mal — hat nichts gebracht")' },
      { id: 'b2_dringlichkeit', label: 'Dringlichkeits-Argumente', placeholder: 'Was erzeugt Handlungsdruck? (z.B. NIS-2 seit Dez 2025 in Kraft — GF haftet JETZT persönlich, BSI-Meldepflicht 24h, Bußgelder bis 10 Mio€, Cyberversicherung verlangt Pentests, Kunden-Audits stehen an?)' },
    ],
  },
  {
    id: 'kampagne',
    title: 'Kampagne & Material',
    subtitle: 'Letzte Details für die Lead-Generierung.',
    fields: [
      { id: 'b2_tonalitaet', label: 'Tonalität der Kampagne', placeholder: 'Wie soll B2CyberSec in den Ads auftreten? (Die Website ist sehr direkt und "kein Bullshit" — soll das auch in der Kampagne so sein? Oder etwas diplomatischer für konservativere Branchen?)' },
      { id: 'b2_geografie', label: 'Geografischer Fokus', placeholder: 'DACH komplett? Nur Deutschland? Bestimmte Regionen oder Ballungsräume? Spielt der Standort des Pentest-Teams (SEE) eine Rolle für die Kundenansprache?' },
      { id: 'b2_bisheriges', label: 'Bisherige Marketing-Aktivitäten', placeholder: 'Wie gewinnen Sie heute Kunden? (Website/NIS-2 Check, LinkedIn, Empfehlungen, Events, Webinare?) Was funktioniert gut und soll ergänzt werden?' },
      { id: 'b2_material', label: 'Vorhandenes Material', placeholder: 'Gibt es bereits das NIS-2 Erklärvideo, Case Studies, Whitepapers, Pentest-Reports (anonymisiert), Testimonials, LinkedIn-Beiträge? Bitte auch unten hochladen.' },
      { id: 'b2_calendly', label: 'Calendly-Links / Terminbuchung', placeholder: 'Bitte die Calendly-Links für die Erstgespräche einfügen: Boris (Pro Services), Senad (NIS-2), Bojan (Pentesting). Oder gibt es einen zentralen Link?' },
    ],
  },
];

/* CS Regionalmarketing — Campus Sparbuch, Studierenden-Marketing */
const SECTIONS_CSRM = [
  {
    id: 'produkt',
    title: 'Produkt & Positionierung',
    subtitle: 'Damit wir die richtigen Partner für das Campus Sparbuch gewinnen.',
    fields: [
      { id: 'cs_produkt', label: 'Welche Pakete/Optionen gibt es für Partner?', placeholder: 'Welche Partnermodelle bieten Sie an? (z.B. Single Deal, Full Branding, Semesterabo?) Preisrahmen pro Paket? Was ist jeweils enthalten (Print, Portal, Social, Promotion)?' },
      { id: 'cs_standorte', label: 'Aktuelle und geplante Hochschulstandorte', placeholder: 'An welchen 50 Standorten sind Sie aktiv? Gibt es Regionen die priorisiert werden sollen? Welche Standorte werden als nächstes erschlossen?' },
      { id: 'cs_usp', label: 'Stärkstes Verkaufsargument', placeholder: 'Warum sollte ein lokales Unternehmen Partner werden? (z.B. 0% Streuverlust, 300.000+ Studierende direkt erreichbar, crossmedial Print+Digital, kein vergleichbares Produkt am Markt?)' },
      { id: 'cs_ergebnisse', label: 'Bisherige Ergebnisse & Kennzahlen', placeholder: 'Welche konkreten Zahlen können wir in den Kampagnen nennen? (z.B. Einlösungsquoten, Anzahl Partner, Reichweite pro Standort?) Gibt es schon echte Testimonials?' },
    ],
  },
  {
    id: 'zielpartner',
    title: 'Der ideale Partner',
    subtitle: 'Welche Unternehmen sollen wir als Termine gewinnen?',
    fields: [
      { id: 'cs_zielbranchen', label: 'Zielbranchen für Partnerschaften', placeholder: 'Welche lokalen Unternehmen funktionieren am besten? (z.B. Restaurants, Fitnessstudios, Friseure, Fahrschulen, Clubs? Oder auch nationale Marken wie Getränke, Fashion, Fintech, Mobilfunk?)' },
      { id: 'cs_entscheider', label: 'Wer entscheidet über die Partnerschaft?', placeholder: 'Inhaber direkt? Marketingleiter? Filialleiter bei Ketten? Regionale Franchise-Nehmer? Bei welcher Unternehmensgröße ist das Budget vorhanden?' },
      { id: 'cs_schmerzpunkt', label: 'Typisches Problem der Zielpartner', placeholder: 'Was frustriert lokale Unternehmen beim Marketing an Studierende? (z.B. Social Media zu teuer, Streuverlust, kein Zugang zur Zielgruppe, keine passende Plattform?)' },
      { id: 'cs_kaufsignal', label: 'Kaufsignale / Wann wird es relevant?', placeholder: 'Wann ist der beste Zeitpunkt für die Ansprache? (z.B. vor Semesterstart, bei Neueröffnung, bei Personalsuche unter Studierenden, wenn Wettbewerber schon im Sparbuch sind?)' },
    ],
  },
  {
    id: 'termin',
    title: 'Das Partnergespräch',
    subtitle: 'Wie überzeugen wir Unternehmen von einer Partnerschaft?',
    fields: [
      { id: 'cs_erstgespraech', label: 'Was erwartet den Partner im Erstgespräch?', placeholder: 'Wie läuft ein typisches Partnergespräch ab? (z.B. Vorstellung des Systems, lokale Reichweiten-Daten, Beispiel-Sparbuch zeigen?) Was bekommt der Interessent kostenlos mit?' },
      { id: 'cs_einwaende', label: 'Häufigste Einwände', placeholder: 'Was hält Unternehmen davon ab, Partner zu werden? (z.B. "Studierende haben kein Geld", "Print ist tot", "Wir machen nur Online", "Zu teuer", "Kennen wir nicht") — Wie entkräften Sie diese?' },
      { id: 'cs_referenzen', label: 'Überzeugendste Referenzen', placeholder: 'Welche bestehenden Partner oder Ergebnisse überzeugen am schnellsten? Gibt es konkrete Erfolgsgeschichten, Einlösungszahlen, Fotos von Campus-Promotions?' },
      { id: 'cs_calendly', label: 'Terminverfügbarkeit / Buchungslink', placeholder: 'Wann sind Sie für Partnergespräche verfügbar? Bitte Ihren Buchungslink einfügen falls vorhanden.' },
    ],
  },
  {
    id: 'kampagne',
    title: 'Kampagne & Material',
    subtitle: 'Was gibt es bereits und wie soll die Ansprache aussehen?',
    fields: [
      { id: 'cs_tonalitaet', label: 'Tonalität der Ansprache', placeholder: 'Wie soll die Kommunikation gegenüber lokalen Unternehmen klingen? (z.B. Jung & dynamisch, Professionell & datengetrieben, Locker & direkt?) Duzen oder Siezen?' },
      { id: 'cs_geografie', label: 'Geografischer Kampagnenfokus', placeholder: 'Welche Regionen/Hochschulstädte sollen priorisiert werden? Gibt es Standorte wo besonders viele Partner gebraucht werden?' },
      { id: 'cs_material', label: 'Vorhandenes Material', placeholder: 'Gibt es bereits Pitch Decks, Beispiel-Sparbücher, Promo-Fotos, Videos, Case Studies, Mediadaten? Bitte auch unten hochladen.' },
      { id: 'cs_sonstiges', label: 'Weitere Anmerkungen', placeholder: 'Alles was wir noch wissen sollten — Semesterstart-Termine, besondere Aktionen, Wettbewerber, Erfahrungen mit früherer Partnerakquise etc.' },
    ],
  },
];

/* ZugoUmzugo — Eingehende Leads closen, CRM-Integration */
const SECTIONS_ZUGO = [
  {
    id: 'unternehmen',
    title: 'ZugoUmzugo — Ihr Angebot',
    subtitle: 'Damit wir Ihre eingehenden Leads optimal bearbeiten können.',
    fields: [
      { id: 'zg_leistungen', label: 'Kernleistungen & Fokus', placeholder: 'Welche Services stehen im Vordergrund? (Privatumzüge, Firmenumzüge, Seniorenumzüge, Entrümpelung, Möbelmontage, Packservice?) Welcher Bereich hat die höchste Marge?' },
      { id: 'zg_einzugsgebiet', label: 'Einzugsgebiet & Niederlassungen', placeholder: 'Saarland (Hauptsitz), Rhein-Main, NRW (Havixbeck) — stimmt das noch? Gibt es Regionen wo besonders viel Nachfrage ist oder wo Sie expandieren wollen?' },
      { id: 'zg_preisstruktur', label: 'Preisstruktur & Festpreismodell', placeholder: 'Wie funktioniert die Festpreiskalkulation? Ab welchem Auftragswert lohnt sich ein Umzug? (z.B. Min. Auftragswert, durchschnittlicher Privatumzug in EUR, Firmenumzug?) Gibt es Staffelpreise?' },
      { id: 'zg_kapazitaet', label: 'Aktuelle Kapazität & Teams', placeholder: 'Wie viele Umzüge pro Woche/Monat können Sie stemmen? Wie viele Teams sind im Einsatz? Gibt es saisonale Engpässe (z.B. Sommer, Monatsanfang/-ende)?' },
    ],
  },
  {
    id: 'leadprozess',
    title: 'Der Lead-Prozess',
    subtitle: 'Wie sollen eingehende Anfragen bearbeitet werden?',
    fields: [
      { id: 'zg_leadquellen', label: 'Woher kommen Ihre Leads aktuell?', placeholder: 'Website-Formular, Google Ads, Telefonanrufe, Empfehlungen, Umzugsportale (z.B. Umzugsauktion)? Welcher Kanal liefert die besten Leads? Welcher die meisten?' },
      { id: 'zg_crm', label: 'Welches CRM nutzen Sie?', placeholder: 'Welches System nutzen Sie zur Lead-Verwaltung? (z.B. HubSpot, Pipedrive, Excel, eigenes System?) Wie sollen wir die Leads dort eintragen oder bearbeiten?' },
      { id: 'zg_qualifizierung', label: 'Wie soll ein Lead qualifiziert werden?', placeholder: 'Welche Infos brauchen Sie von einem Lead? (z.B. Umzugsdatum, Von-/Nach-Adresse, Wohnungsgröße, Etage, Möbelliste, Budget?) Ab wann ist ein Lead "heiß"?' },
      { id: 'zg_reaktionszeit', label: 'Gewünschte Reaktionszeit', placeholder: 'Wie schnell soll ein Lead kontaktiert werden? (z.B. innerhalb 30 Min, 2 Stunden, am selben Tag?) Was passiert wenn der Lead nicht sofort erreicht wird — Follow-up Prozess?' },
    ],
  },
  {
    id: 'closing',
    title: 'Das Verkaufsgespräch / Closing',
    subtitle: 'Wie sollen wir Leads zu Kunden machen?',
    fields: [
      { id: 'zg_gespraech', label: 'Wie läuft ein typisches Verkaufsgespräch?', placeholder: 'Was passiert nach der Anfrage? (z.B. Telefon-Qualifizierung → kostenlose Besichtigung → Festpreis-Angebot → Buchung?) Welche Schritte sollen wir übernehmen, welche machen Sie selbst?' },
      { id: 'zg_einwaende', label: 'Häufigste Einwände der Kunden', placeholder: 'Was sagen Interessenten bevor sie buchen? (z.B. "Zu teuer", "Anderes Angebot ist günstiger", "Machen wir selbst", "Termin passt nicht") — Wie entkräften Sie diese?' },
      { id: 'zg_usp_closing', label: 'Stärkste Argumente im Gespräch', placeholder: 'Was überzeugt Kunden final? (z.B. Festpreisgarantie, 5/5 Google-Bewertung, Versicherung bis 2 Mio€, Video-Testimonials, "Wir behandeln Ihren Umzug wie unseren eigenen"?)' },
      { id: 'zg_besichtigung', label: 'Besichtigung: Vor Ort oder Video?', placeholder: 'Wie funktioniert die kostenlose Besichtigung? Vor Ort oder per Video-Call? Wie schnell kann ein Termin vereinbart werden? Wer macht die Besichtigung?' },
    ],
  },
  {
    id: 'details',
    title: 'Details & Material',
    subtitle: 'Letzte Informationen für die Zusammenarbeit.',
    fields: [
      { id: 'zg_oeffnungszeiten', label: 'Erreichbarkeit & Öffnungszeiten', placeholder: 'Wann ist das Team erreichbar? (z.B. Mo-Sa 7-19 Uhr, 24/7, nur unter der Woche?) Wer nimmt Anrufe entgegen? Gibt es eine zentrale Nummer?' },
      { id: 'zg_tonalitaet', label: 'Tonalität gegenüber Kunden', placeholder: 'Wie soll mit den Leads gesprochen werden? (z.B. Freundlich & empathisch, Professionell & effizient, Locker & unkompliziert?) Duzen oder Siezen?' },
      { id: 'zg_material', label: 'Vorhandenes Material', placeholder: 'Gibt es Angebotsvorlagen, Checklisten, FAQ-Dokumente, CRM-Zugangsdaten, Skripte für Telefonate? Bitte auch unten hochladen.' },
      { id: 'zg_sonstiges', label: 'Weitere Anmerkungen', placeholder: 'Alles was wir noch wissen sollten — Peak-Zeiten, No-Gos, besondere Kundenwünsche, Erfahrungen mit früheren Dienstleistern etc.' },
    ],
  },
];

/* WeDoIT GmbH — Managed Cybersecurity, KRITIS, Cynet, PKI */
const SECTIONS_WEDOIT = [
  {
    id: 'schwerpunkt',
    title: 'Kampagnenfokus & Zielgruppe',
    subtitle: 'Welche Unternehmen sollen wir für WeDoIT als Termine gewinnen?',
    fields: [
      { id: 'wd_fokusprodukt', label: 'Welches Produkt/Lösung steht im Kampagnenfokus?', placeholder: 'Was soll primär beworben werden? (z.B. Cynet 360° AutoXDR Plattform, Managed SOC, BSI Cyber-Risiko-Check, PKI mit Thales, KRITIS-Compliance?) Gewichtung wenn mehrere?' },
      { id: 'wd_mindestgroesse', label: 'Mindestgröße: ab 100 Endpoints — was bedeutet das konkret?', placeholder: 'Ab 100 Endpoints = ca. wie viele Mitarbeitende? Welche Unternehmensgröße ist ideal? (z.B. 100-500 MA, 500-2000 MA?) Gibt es eine Obergrenze? Ab welcher Größe wird es besonders lukrativ?' },
      { id: 'wd_kritis_branchen', label: 'KRITIS-Branchen & Sektoren priorisieren', placeholder: 'Welche der KRITIS-Sektoren wollen Sie priorisieren? (Energie, Gesundheit/Spitäler, Banken/Versicherungen, IT-Dienste, Logistik/Transport, Wasser, Lebensmittel, Öffentliche Verwaltung, Industrie/Produktion?) Wo haben Sie die stärksten Referenzen?' },
      { id: 'wd_entscheider', label: 'Wer entscheidet über die Beauftragung?', placeholder: 'Wer sitzt bei Unternehmen ab 100 Endpoints typischerweise am Tisch? (IT-Leiter, CISO, GF wegen NIS-2 Haftung, CTO, Head of Infrastructure, Compliance Officer?) Unterschiede je nach Unternehmensgröße?' },
    ],
  },
  {
    id: 'angebot',
    title: 'Angebot & Differenzierung',
    subtitle: 'Was macht WeDoIT zum richtigen Partner?',
    fields: [
      { id: 'wd_usp', label: 'Stärkstes Argument gegenüber Wettbewerb', placeholder: 'Warum WeDoIT und nicht ein anderer MSSP oder die Big-4? (z.B. exklusiver Cynet Partner, BSI-zertifiziert, 24/7 SOC, 300+ Kunden, 20+ Jahre Erfahrung, Cybersecurity + PKI aus einer Hand?) Was überzeugt am schnellsten?' },
      { id: 'wd_einstieg', label: 'Typischer Einstiegspunkt beim Kunden', placeholder: 'Wie steigen Neukunden typischerweise ein? (z.B. kostenloser Cyber-Risiko-Check nach BSI, Proof-of-Concept mit Cynet, NIS-2 Readiness Assessment?) Was ist der niedrigschwelligste Einstieg?' },
      { id: 'wd_preisrahmen', label: 'Preisrahmen & Lizenzmodell', placeholder: 'Wie sieht die Preisstruktur aus? (z.B. pro Endpoint/Monat, Einmal-Assessment, Retainer-Modell?) Ab welchem Budget ist ein Einstieg realistisch? Was kostet Cynet pro Endpoint ungefähr?' },
      { id: 'wd_referenzen', label: 'Stärkste Referenzen & Ergebnisse', placeholder: 'Welche Kunden oder Projekte überzeugen sofort? (z.B. bekannte KRITIS-Betreiber, Anzahl realisierter Projekte, bestimmte Branchen?) Dürfen Namen genannt werden? Gibt es Case Studies oder Zahlen?' },
    ],
  },
  {
    id: 'termin',
    title: 'Das Erstgespräch',
    subtitle: 'Was erwartet den potenziellen Kunden?',
    fields: [
      { id: 'wd_erstgespraech', label: 'Ablauf des Erstgesprächs', placeholder: 'Was passiert im 30-Min Erstgespräch? (z.B. Analyse der aktuellen Security-Posture, Vorstellung Cynet Live-Demo, BSI Cyber-Check erklären, konkreter Maßnahmenplan?) Was nimmt der Kunde mit — auch wenn er nicht kauft?' },
      { id: 'wd_einwaende', label: 'Häufigste Einwände', placeholder: 'Was hält IT-Leiter/CISOs davon ab zu buchen? (z.B. "Haben schon eine Lösung", "Zu teuer", "Machen wir intern", "Kein Budget dieses Jahr", "NIS-2 betrifft uns nicht", "Brauchen erst Vorstandsfreigabe")' },
      { id: 'wd_dringlichkeit', label: 'Dringlichkeits-Argumente (KRITIS/NIS-2)', placeholder: 'Was erzeugt Handlungsdruck? (z.B. NIS-2 GF-Haftung seit Dez 2025, BSI-Meldepflicht 24h, Bußgelder bis 10 Mio€, Cyberversicherung verlangt Nachweise, Ransomware-Angriff bei Wettbewerber, BSI-Audit steht an?)' },
      { id: 'wd_team', label: 'Wer führt die Erstgespräche?', placeholder: 'Welche Person(en) führen die Termine? Sollen Termine direkt über einen Calendly-Link laufen? Bitte Link einfügen falls vorhanden.' },
    ],
  },
  {
    id: 'kampagne',
    title: 'Kampagne & Material',
    subtitle: 'Letzte Details für die Terminierung.',
    fields: [
      { id: 'wd_tonalitaet', label: 'Tonalität der Kampagne', placeholder: 'Wie soll WeDoIT in der Ansprache wirken? (z.B. Technisch-kompetent, Beratend & lösungsorientiert, Direkt & dringend wegen NIS-2?) Siezen ist vermutlich Standard — stimmt das?' },
      { id: 'wd_geografie', label: 'Geografischer Fokus', placeholder: 'DACH komplett? Nur Deutschland? Bestimmte Regionen? Gibt es Gebiete mit besonders hoher KRITIS-Dichte (z.B. NRW, Bayern, Großstädte)?' },
      { id: 'wd_bisheriges', label: 'Bisherige Akquise-Kanäle', placeholder: 'Wie gewinnen Sie heute Kunden? (Website, Partner-Netzwerk, Events/Messen, LinkedIn, Google Ads, BSI-Veranstaltungen?) Was funktioniert gut?' },
      { id: 'wd_material', label: 'Vorhandenes Material', placeholder: 'Gibt es bereits Whitepapers, Case Studies, Cynet-Demovideos, BSI-Zertifikate, Pitch Decks, NIS-2 Checklisten? Bitte auch unten hochladen.' },
    ],
  },
];

/* Orkanor — B2B Energiedistribution (Strom, Gas, TK) für Gewerbe-/Industriekunden */
const SECTIONS_ORKANOR = [
  {
    id: 'zielkunde',
    title: 'Zielkunden & Markt',
    subtitle: 'Welche Unternehmen sollen wir als Termine für Orkanor gewinnen?',
    fields: [
      { id: 'ok_zielbranchen', label: 'Zielbranchen / Unternehmenstypen', placeholder: 'Welche Branchen sind am lukrativsten? (z.B. Produktion/Industrie, Handel/Logistik, Handwerksbetriebe, Hotels/Gastronomie, Bürokomplexe, Kommunen?) Gibt es Branchen mit besonders hohem Energieverbrauch die priorisiert werden sollen?' },
      { id: 'ok_mindestverbrauch', label: 'Mindestverbrauch / Mindestgröße', placeholder: 'Ab welchem Jahresverbrauch (kWh) oder ab welcher Unternehmensgröße lohnt sich ein Gespräch? Gibt es ein Minimum an Mitarbeitenden oder Standorten? Was ist der ideale Kunden-Sweet-Spot?' },
      { id: 'ok_entscheider', label: 'Wer entscheidet über den Energieanbieter?', placeholder: 'Geschäftsführer, Facility Manager, Einkaufsleiter, kaufmännische Leitung, Office Manager? Unterschiede je nach Unternehmensgröße?' },
      { id: 'ok_geografie', label: 'Geografischer Fokus', placeholder: 'Ganz Deutschland? Bestimmte Bundesländer oder Regionen? Gibt es Gebiete mit besonders hohem Potenzial oder bestehender Abdeckung?' },
    ],
  },
  {
    id: 'angebot',
    title: 'Angebot & Differenzierung',
    subtitle: 'Was macht Orkanor für B2B-Kunden attraktiv?',
    fields: [
      { id: 'ok_produkte', label: 'Welche Produkte stehen im Fokus?', placeholder: 'Strom, Gas, Telekommunikation — was soll primär beworben werden? Gibt es Kombi-Pakete? Besondere Tarife für Gewerbe/Industrie? Was ist der Einstieg für Neukunden?' },
      { id: 'ok_usp', label: 'Stärkstes Verkaufsargument vs. Wettbewerb', placeholder: 'Warum Orkanor statt direkt zum Versorger? (z.B. Anbietervergleich, unabhängige Beratung, bessere Konditionen, persönlicher Ansprechpartner, 800+ Partner, seit 2012 am Markt?) Was überzeugt Geschäftsführer am schnellsten?' },
      { id: 'ok_ersparnis', label: 'Typische Ersparnis / Konditionen', placeholder: 'Wie viel können Gewerbekunden typischerweise sparen? (z.B. X% vs. Grundversorger, Y€ pro Jahr bei Z kWh Verbrauch?) Gibt es Beispielrechnungen oder Referenzwerte?' },
      { id: 'ok_referenzen', label: 'Stärkste Referenzen & Erfolgsbeispiele', placeholder: 'Welche Kunden oder Branchen können als Beispiel genannt werden? Gibt es Zahlen (z.B. "150 Gewerbebetriebe in NRW versorgt", "durchschnittlich 18% Ersparnis")? Dürfen Namen genannt werden?' },
    ],
  },
  {
    id: 'termin',
    title: 'Das Erstgespräch / Beratung',
    subtitle: 'Was erwartet den Gewerbekunden im Termin?',
    fields: [
      { id: 'ok_erstgespraech', label: 'Ablauf des Erstgesprächs', placeholder: 'Was passiert im Beratungsgespräch? (z.B. Verbrauchsanalyse, Tarifvergleich, unverbindliches Angebot, Wechselprozess erklären?) Wie lange dauert es? Was nimmt der Kunde mit — auch wenn er nicht wechselt?' },
      { id: 'ok_einwaende', label: 'Häufigste Einwände', placeholder: 'Was hält Gewerbekunden ab? (z.B. "Bin noch im Vertrag", "Klingt zu gut um wahr zu sein", "Wechsel ist kompliziert", "Habe schon guten Tarif", "Keine Zeit für sowas") — Wie entkräften Sie das?' },
      { id: 'ok_wechselprozess', label: 'Wie einfach ist der Wechsel?', placeholder: 'Was muss der Kunde tun? Wie läuft die Kündigung beim alten Anbieter? Wie schnell geht der Wechsel? Gibt es eine Versorgungsunterbrechung? Was übernimmt Orkanor komplett?' },
      { id: 'ok_team', label: 'Wer führt die Beratungsgespräche?', placeholder: 'Eigener Außendienst, Vertriebsinnendienst, Tilman persönlich? Sollen Termine über einen Calendly/Buchungslink laufen? Bitte Link einfügen falls vorhanden.' },
    ],
  },
  {
    id: 'kampagne',
    title: 'Kampagne & Material',
    subtitle: 'Letzte Details für die Terminierung.',
    fields: [
      { id: 'ok_tonalitaet', label: 'Tonalität & Ansprache', placeholder: 'Wie soll Orkanor gegenüber Gewerbekunden auftreten? (z.B. Beratend & vertrauensvoll, Direkt & zahlengetrieben, Persönlich & nahbar?) Duzen oder Siezen? Soll der Fokus auf Ersparnis, Unabhängigkeit oder Service liegen?' },
      { id: 'ok_bisheriges', label: 'Bisherige Akquise-Kanäle', placeholder: 'Wie gewinnen Sie heute Gewerbekunden? (Partnernetzwerk, D2D, Empfehlungen, Online, Events?) Was funktioniert gut, was nicht?' },
      { id: 'ok_material', label: 'Vorhandenes Material', placeholder: 'Gibt es Broschüren, Tarifübersichten, Vergleichsrechner, Pitch Decks, Referenzschreiben, Muster-Angebote? Bitte auch unten hochladen.' },
      { id: 'ok_sonstiges', label: 'Weitere Anmerkungen', placeholder: 'Alles was wir noch wissen sollten — Vertragslaufzeiten, saisonale Besonderheiten, No-Gos, Erfahrungen mit Agenturen, besondere Zielkunden-Wünsche etc.' },
    ],
  },
];

/* Carport & Wintergarten — Alu-Überdachungen, Leads per Online-Ads */
const SECTIONS_CUW = [
  {
    id: 'produkte',
    title: 'Produkte & Leistungen',
    subtitle: 'Was genau bietet ihr an — damit wir die Ads richtig aufbauen können.',
    fields: [
      { id: 'cuw_kernprodukte', label: 'Welche Produkte stehen im Fokus?', placeholder: 'z.B. Terrassenüberdachungen, Carports, Wintergärten, Gartenzimmer, Sommergärten? Welches Produkt hat die höchste Nachfrage / beste Marge? Gibt es saisonale Schwerpunkte?' },
      { id: 'cuw_materialien', label: 'Material & Besonderheiten', placeholder: 'z.B. Aluminium-Konstruktionen, Glas-Schiebewände, LED-Beleuchtung, Heizstrahler-Integration? Was unterscheidet eure Produkte von der Konkurrenz?' },
      { id: 'cuw_preisrahmen', label: 'Preisrahmen & Einstieg', placeholder: 'Was kostet eine typische Terrassenüberdachung / ein Carport / Wintergarten ungefähr? (z.B. "ab 5.000€", "8.000-25.000€ je nach Größe") Ab welchem Budget lohnt sich eine Anfrage?' },
      { id: 'cuw_prozess', label: 'Ablauf von Anfrage bis Montage', placeholder: 'Wie läuft es ab? (z.B. Anfrage → WhatsApp-Kontakt → Vor-Ort-Aufmaß → Angebot in 24h → Montage in X Wochen?) Wie schnell bekommt der Kunde ein Angebot?' },
    ],
  },
  {
    id: 'zielgruppe',
    title: 'Zielgruppe & Region',
    subtitle: 'Wen sollen die Ads erreichen?',
    fields: [
      { id: 'cuw_zielgruppe', label: 'Wer ist euer idealer Kunde?', placeholder: 'z.B. Eigenheimbesitzer 35-65, Neubauten, Hausbesitzer mit Terrasse/Garten, bestimmtes Einkommen? Eher ländlich oder städtisch? Gibt es typische Trigger? (z.B. Neubau, Renovierung, Sommer naht)' },
      { id: 'cuw_einzugsgebiet', label: 'Einzugsgebiet / Montageradius', placeholder: 'In welchem Umkreis montiert ihr? (z.B. Mecklenburg-Vorpommern, Norddeutschland, bundesweit?) Gibt es Regionen wo ihr besonders stark seid oder expandieren wollt?' },
      { id: 'cuw_konkurrenz', label: 'Wettbewerb & USP', placeholder: 'Wer sind die größten Konkurrenten in eurer Region? Was macht euch besser? (z.B. Angebot in 24h per WhatsApp, eigenes Montageteam, Alles aus einer Hand inkl. Baugenehmigung, Preis-Leistung?)' },
      { id: 'cuw_saison', label: 'Saisonalität & Timing', placeholder: 'Wann ist Hochsaison / Nebensaison? In welchen Monaten kommen die meisten Anfragen? Gibt es eine Vorlaufzeit für Planung & Montage die Kunden wissen sollten?' },
    ],
  },
  {
    id: 'ads_content',
    title: 'Ads & Quiz-Funnel',
    subtitle: 'Inhalte für die Online-Kampagnen und den Qualifizierungs-Funnel.',
    fields: [
      { id: 'cuw_hook', label: 'Stärkstes Verkaufsargument für Ads', placeholder: 'Was überzeugt Interessenten am schnellsten? (z.B. "Angebot in 24h", "Kostenlose Beratung", "Montage in X Wochen", "Made in Germany", "10 Jahre Garantie"?) Was soll der erste Eindruck sein?' },
      { id: 'cuw_quiz_fragen', label: 'Wichtige Qualifizierungsfragen für den Funnel', placeholder: 'Welche Infos braucht ihr von einem Lead? (z.B. Welches Produkt?, Grundstück vorhanden?, Gewünschte Größe?, Budget?, PLZ/Region?, Zeitrahmen?) Was disqualifiziert einen Lead?' },
      { id: 'cuw_einwaende', label: 'Häufigste Einwände / Bedenken', placeholder: 'Was hält Interessenten ab? (z.B. "Zu teuer", "Brauche erst Baugenehmigung", "Will erst vergleichen", "Geht das bei meinem Grundstück?") Wie entkräftet ihr das?' },
      { id: 'cuw_social_proof', label: 'Referenzen & Kundenstimmen', placeholder: 'Welche Projekte können gezeigt werden? Gibt es Vorher/Nachher-Fotos, Google-Bewertungen, Video-Testimonials? Welche Orte/Projekte eignen sich am besten für Ads?' },
    ],
  },
  {
    id: 'material',
    title: 'Material & Uploads',
    subtitle: 'Fotos, Dokumente und alles was wir für die Kampagnen brauchen.',
    fields: [
      { id: 'cuw_fotos', label: 'Verfügbare Fotos & Videos', placeholder: 'Welche Projektfotos sind vorhanden? (Vorher/Nachher, fertige Projekte, Montage-Prozess, Drohnenaufnahmen?) Bitte die besten Bilder unten hochladen!' },
      { id: 'cuw_email', label: 'E-Mail für Lead-Benachrichtigungen', placeholder: 'An welche E-Mail-Adresse(n) sollen neue Anfragen/Leads geschickt werden? (z.B. info@carportundwintergarten.de, kristina@..., ron@...) Mehrere möglich.' },
      { id: 'cuw_telefon_zeiten', label: 'Telefonische Beratung — Verfügbarkeit', placeholder: 'Wann können telefonische Beratungstermine mit Interessenten stattfinden? (z.B. Mo-Fr 9-17 Uhr, auch Sa möglich?) Wer führt die Gespräche — Kristina, Ron, oder beide?' },
      { id: 'cuw_video_termine', label: 'Video-Termine gewünscht?', placeholder: 'Sollen wir auch Video-Beratungstermine (z.B. Zoom/Google Meet) anbieten? Oder reicht telefonisch + Vor-Ort? Falls ja — gibt es einen bevorzugten Anbieter?' },
      { id: 'cuw_vorort_zeiten', label: 'Vor-Ort-Termine / Aufmaß — Verfügbarkeit', placeholder: 'Wann können Vor-Ort-Termine (Aufmaß/Beratung) angeboten werden? Welche Tage/Uhrzeiten? Wie viel Vorlauf braucht ihr? Gibt es einen Buchungslink (Calendly o.ä.) oder sollen wir einen einrichten?' },
      { id: 'cuw_bisheriges', label: 'Bisheriges Marketing', placeholder: 'Welche Werbung habt ihr bisher gemacht? (Google Ads, Facebook/Instagram, Flyer, Messen?) Was hat funktioniert, was nicht? Gibt es einen bestehenden Werbe-Account?' },
      { id: 'cuw_tonalitaet', label: 'Tonalität & Markenstimme', placeholder: 'Wie soll die Ansprache sein? (z.B. Premium & hochwertig, Bodenständig & handwerklich, Modern & innovativ?) Duzen oder Siezen gegenüber Kunden?' },
      { id: 'cuw_sonstiges', label: 'Weitere Anmerkungen', placeholder: 'Alles was wir noch wissen sollten — besondere Aktionen, Kooperationen, No-Gos, Erfahrungen mit Agenturen, Wünsche für die Kampagne etc.' },
    ],
  },
];

const QUESTIONNAIRE_MAP = {
  default: SECTIONS,
  wpp: SECTIONS_WPP,
  targettv: SECTIONS_TARGETTV,
  lionx: SECTIONS_LIONX,
  samplett: SECTIONS_SAMPLETT,
  livejobs: SECTIONS_LIVEJOBS,
  allectio: SECTIONS_ALLECTIO,
  b2cybersec: SECTIONS_B2CYBERSEC,
  csrm: SECTIONS_CSRM,
  zugo: SECTIONS_ZUGO,
  wedoit: SECTIONS_WEDOIT,
  orkanor: SECTIONS_ORKANOR,
  cuw: SECTIONS_CUW,
};


/* =========== CODE ENTRY =========== */
function CodeEntry({ onVerified }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/onboarding/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });
      if (!res.ok) {
        const err = await res.json();
        setError(err.detail || 'Ungültiger Code');
        setLoading(false);
        return;
      }
      const data = await res.json();
      onVerified({ code: code.trim(), ...data });
    } catch {
      setError('Verbindungsfehler. Bitte versuche es erneut.');
    }
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 text-center relative overflow-hidden bg-slate-50"
      data-testid="onboarding-code-entry"
    >
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-[0.04]"
        style={{ background: 'radial-gradient(circle, #2E7CF6 0%, transparent 70%)' }}
      />
      <div className="relative z-10 max-w-md w-full">
        <div className="mb-12">
          <span className="text-slate-900 font-black text-xl tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            VALORYX
          </span>
        </div>
        <div className="w-16 h-16 rounded-full bg-[#2E7CF6]/10 border border-[#2E7CF6]/20 flex items-center justify-center mx-auto mb-8">
          <Lock className="w-7 h-7 text-[#2E7CF6]" />
        </div>
        <h1
          className="text-3xl sm:text-4xl text-slate-900 leading-tight mb-3 font-black tracking-tighter"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Client Onboarding
        </h1>
        <p className="text-base text-slate-500 mb-10" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Gib deinen persönlichen Zugangscode ein, um fortzufahren.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            data-testid="onboarding-code-input"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(''); }}
            placeholder="Zugangscode"
            className="w-full bg-slate-50 border border-slate-200 rounded-md px-5 py-4 text-slate-900 text-center text-lg tracking-widest placeholder:text-slate-400 focus:outline-none focus:border-[#2E7CF6]/50 transition-colors"
            autoFocus
          />
          {error && <p className="text-red-500 text-sm" data-testid="code-error">{error}</p>}
          <button
            type="submit"
            data-testid="onboarding-code-submit"
            disabled={loading || !code.trim()}
            className="w-full px-8 py-4 text-white font-bold text-base rounded-md transition-all disabled:opacity-40 hover:opacity-90 cursor-pointer bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4]"
          >
            {loading ? 'Wird geprüft...' : 'Weiter'}
          </button>
        </form>
      </div>
      <footer className="absolute bottom-6 left-0 right-0 text-center text-slate-300 text-xs px-6">
        2026 VALORYX.{' '}
        <Link to="/impressum" className="underline hover:text-slate-500">Impressum</Link>
        {' - '}
        <Link to="/datenschutz" className="underline hover:text-slate-500">Datenschutzerklärung</Link>
      </footer>
    </div>
  );
}


/* =========== FILE UPLOAD =========== */
function FileUploadArea({ files, setFiles }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (fileList) => {
    setUploading(true);
    for (const file of fileList) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await fetch(`${API_URL}/api/onboarding/upload`, {
          method: 'POST',
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          setFiles(prev => [...prev, { ...data }]);
        }
      } catch (e) {
        console.error('Upload error:', e);
      }
    }
    setUploading(false);
  };

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.file_id !== fileId));
  };

  const getIcon = (name) => {
    const ext = name.split('.').pop().toLowerCase();
    if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) return <Image className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  return (
    <div className="mt-8">
      <label className="block text-sm font-bold text-slate-700 mb-3 tracking-wide uppercase" style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.08em' }}>
        Dokumente hochladen
      </label>
      <p className="text-xs text-slate-400 mb-4">Logo, Branding-Materialien, bestehende Ads, Referenz-Dokumente etc. (Alle Dateitypen erlaubt — max. 20MB pro Datei)</p>

      <div
        className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-[#2E7CF6]/30 transition-colors cursor-pointer"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDragLeave={(e) => {}}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        data-testid="file-drop-zone"
      >
        {uploading ? (
          <Loader2 className="w-8 h-8 text-[#2E7CF6] mx-auto animate-spin" />
        ) : (
          <Upload className="w-8 h-8 text-slate-300 mx-auto mb-3" />
        )}
        <p className="text-sm text-slate-400">{uploading ? 'Wird hochgeladen...' : 'Dateien hierher ziehen oder klicken'}</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="*/*"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="file-input"
        />
      </div>

      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          {files.map((f) => (
            <div key={f.file_id} className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3" data-testid={`uploaded-file-${f.file_id}`}>
              <span className="text-[#2E7CF6]">{getIcon(f.filename)}</span>
              <span className="text-sm text-slate-700 truncate flex-1">{f.filename}</span>
              <span className="text-xs text-slate-400">{(f.size / 1024).toFixed(0)} KB</span>
              <button onClick={() => removeFile(f.file_id)} className="text-slate-300 hover:text-red-400 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* =========== THANK YOU =========== */
function ThankYou({ companyName }) {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 text-center relative overflow-hidden bg-slate-50"
      data-testid="onboarding-thank-you"
    >
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full opacity-[0.04]"
        style={{ background: 'radial-gradient(circle, #2E7CF6 0%, transparent 70%)' }}
      />
      <div className="relative z-10 max-w-lg">
        <div className="w-16 h-16 rounded-full bg-[#2E7CF6]/10 border border-[#2E7CF6]/20 flex items-center justify-center mx-auto mb-8">
          <Check className="w-8 h-8 text-[#2E7CF6]" />
        </div>
        <h1
          className="text-3xl sm:text-4xl text-slate-900 leading-tight mb-4 font-black tracking-tighter"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Vielen Dank!
        </h1>
        <p className="text-lg text-slate-600 leading-relaxed mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Wir haben alle Informationen von <strong className="text-slate-900">{companyName}</strong> erhalten.
        </p>
        <p className="text-base text-slate-500 leading-relaxed mb-10" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Unser Team wertet Ihre Angaben aus und meldet sich in Kürze mit dem nächsten Schritt.
        </p>
        <a
          href="/"
          className="inline-block px-8 py-3 border border-slate-200 text-slate-600 font-medium rounded-md hover:bg-slate-50 transition-all text-sm"
        >
          Zur Startseite
        </a>
      </div>
    </div>
  );
}


/* =========== MAIN QUESTIONNAIRE =========== */
export default function OnboardingPage() {
  const [verified, setVerified] = useState(null);
  const [sectionIdx, setSectionIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!verified) {
    return <CodeEntry onVerified={setVerified} />;
  }

  if (submitted) {
    return <ThankYou companyName={verified.company_name} />;
  }

  const sections = QUESTIONNAIRE_MAP[verified.questionnaire] || SECTIONS;
  const section = sections[sectionIdx];
  const isLast = sectionIdx === sections.length - 1;
  const progress = ((sectionIdx + 1) / sections.length) * 100;

  const updateAnswer = (fieldId, value) => {
    setAnswers(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/onboarding/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: verified.code,
          answers,
          files,
        }),
      });
    } catch (e) {
      console.error('Submit error:', e);
    }
    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden bg-slate-50"
      data-testid="onboarding-form"
    >
      {/* Ambient glow */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] pointer-events-none opacity-[0.04]"
        style={{ background: 'radial-gradient(circle, #2E7CF6 0%, transparent 70%)' }}
      />

      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-slate-100 z-50">
        <div
          className="h-full transition-all duration-500 ease-out rounded-r-full bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4]"
          style={{ width: `${progress}%` }}
          data-testid="onboarding-progress"
        />
      </div>

      {/* Top bar */}
      <div className="fixed top-5 left-6 right-6 z-40 flex items-center justify-between">
        <div>
          <span className="text-slate-900 font-black text-lg tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            VALORYX
          </span>
          <span className="text-slate-400 text-xs ml-3">Onboarding</span>
        </div>
        <div className="text-right">
          <span className="text-sm text-slate-400">{sectionIdx + 1} / {sections.length}</span>
          <p className="text-xs text-[#2E7CF6] mt-0.5">{verified.company_name}</p>
        </div>
      </div>

      {/* Content */}
      <div className="min-h-screen flex items-center justify-center px-6 py-28">
        <div className="w-full max-w-2xl" key={sectionIdx}>
          {/* Section header */}
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#2E7CF6]/20 bg-[#2E7CF6]/5 mb-6">
              <span className="text-[11px] font-bold tracking-[0.15em] uppercase text-[#2E7CF6]" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Abschnitt {sectionIdx + 1}
              </span>
            </div>
            <h2
              className="text-2xl sm:text-3xl text-slate-900 leading-snug mb-3 font-bold tracking-tight"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {section.title}
            </h2>
            <p className="text-sm text-slate-500" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {section.subtitle}
            </p>
          </div>

          {/* Fields */}
          <div className="space-y-8">
            {section.fields.map((field) => (
              <div key={field.id}>
                <label className="block text-sm font-bold text-slate-700 mb-2.5 tracking-wide" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {field.label}
                </label>
                <textarea
                  data-testid={`field-${field.id}`}
                  value={answers[field.id] || ''}
                  onChange={(e) => updateAnswer(field.id, e.target.value)}
                  placeholder={field.placeholder}
                  rows={4}
                  className="w-full bg-slate-50/70 border border-slate-200 rounded-md px-5 py-4 text-slate-900 text-sm leading-relaxed placeholder:text-slate-500 focus:outline-none focus:border-[#2E7CF6]/40 transition-colors resize-none"
                />
              </div>
            ))}
          </div>

          {/* File upload on last section */}
          {isLast && <FileUploadArea files={files} setFiles={setFiles} />}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-12">
            <button
              onClick={() => setSectionIdx(s => s - 1)}
              disabled={sectionIdx === 0}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-700 transition-colors disabled:opacity-0 disabled:pointer-events-none"
              data-testid="onboarding-prev"
            >
              <ArrowLeft className="w-4 h-4" /> Zurück
            </button>

            {isLast ? (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="onboarding-submit"
                className="flex items-center gap-2 px-8 py-3.5 text-white font-bold text-sm rounded-md transition-all disabled:opacity-40 hover:opacity-90 cursor-pointer bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4]"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Wird gesendet...</>
                ) : (
                  <><Check className="w-4 h-4" /> Fragebogen absenden</>
                )}
              </button>
            ) : (
              <button
                onClick={() => setSectionIdx(s => s + 1)}
                data-testid="onboarding-next"
                className="flex items-center gap-2 px-6 py-3 text-white font-bold text-sm rounded-md transition-all hover:opacity-90 cursor-pointer bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4]"
              >
                Weiter <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
